import {
  CreateBookingInput,
  V2Gateway_GetCustomerInputsForServiceOfferingQueryHookResult,
  CustomerInputType,
  V2Gateway_GetServiceOfferingsForServiceProviderQueryHookResult,
} from 'Generated/graphql';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Bounds } from './Availability';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

type ServiceOfferings = NonNullable<
  V2Gateway_GetServiceOfferingsForServiceProviderQueryHookResult['data']
>[
  'queryServiceOfferings'
]['serviceOfferings'];

export const getDisabledHoursForAppointment = (
  startDateTime: number,
  endDateTime: number,
): number[] => {
  const appointmentStart = dayjs.unix(startDateTime);
  const appointmentEnd = dayjs.unix(endDateTime);
  const allHours = [...Array(24).keys()];

  const disabledHours = allHours.filter(hour => {
    const beforeStart = hour < appointmentStart.hour();
    const afterEnd = hour > appointmentEnd.hour();
    return beforeStart || afterEnd;
  });
  return disabledHours;
};

// This function must match the logic in
// ./src/gitlab.com/mobl-dev/service/booking/booking-oracle/internal/logic/\
// validator/logic.go
export const isMomentRangeOverlapWithExistingBooking = (
  bookingRanges: [number, number][],
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
): boolean => {
  const conflictingBookings = bookingRanges.some(booking => {
    const [startDateTime, endDateTime] = booking;
    const bookingStartMoment = dayjs.unix(startDateTime);
    const bookingEndMoment = dayjs.unix(endDateTime);
    const startsDuringBooking = start.isBetween(
      bookingStartMoment, bookingEndMoment, 'm', '[)'
    );
    const endsDuringBooking = end.isBetween(
      bookingStartMoment, bookingEndMoment, 'm', '()'
    );
    const wrapsBooking = start.isSameOrBefore(bookingStartMoment) &&
      end.isSameOrAfter(bookingEndMoment);

    return startsDuringBooking || endsDuringBooking || wrapsBooking;
  });

  return conflictingBookings;
};

type customerInputs = NonNullable<
  V2Gateway_GetCustomerInputsForServiceOfferingQueryHookResult['data']
>['getServiceOfferingCustomerInputs']
type allCustomerInputs = Pick<customerInputs[number], 'uid' | 'type' | 'options' | 'unitPrice'>[]

const getOptionSelectionValues = <T extends allCustomerInputs[number]>(
  optionValue: string,
  ciUid: string,
  allCustomerInputs: T[],
): [T, string][] => {
  const ci = allCustomerInputs.find(c => c.uid === ciUid);
  if (!ci) {
    return [];
  }

  if (ci.type === CustomerInputType.Checkbox) {
    const ov = optionValue ? optionValue.split(',') : [];
    return ov.map(o => [ci, o]);
  }

  return [[ci, optionValue]];
};

type directSelections = Pick<NonNullable<CreateBookingInput['customerInputSelections']>[number], 'customerInputUid' | 'optionValue'>[]

export const calculateTotalsForCustomerInputSelections = <T extends allCustomerInputs[number]>(
  directSelections: directSelections,
  groupSelections: CreateBookingInput['customerInputGroupSelections'],
  allCustomerInputs: T[],
): [number, number] => {
  let allSelectedOptionValues: [T, string][] = [];
  if (groupSelections) {
    const groupSelectionsOptionValues = groupSelections.flatMap(
      g => g.flatMap(
        sels => sels.optionValues.flatMap(
          (optVals, i) => {
            if (sels.customerInputUids) {
              return getOptionSelectionValues(
                optVals, sels.customerInputUids[i], allCustomerInputs,
              );
            } else {
              return [];
            }
          }
        )
      )
    );
    allSelectedOptionValues = allSelectedOptionValues
      .concat(groupSelectionsOptionValues);
  }

  if (directSelections) {
    // flatMap direct selections because checkbox group returns an array
    const directSelectionOptionValues = directSelections.flatMap(
      sel => getOptionSelectionValues(
        sel.optionValue, sel.customerInputUid, allCustomerInputs,
      )
    );
    allSelectedOptionValues = allSelectedOptionValues
      .concat(directSelectionOptionValues);
  }

  let newTotalPrice = 0;
  let newTotalDuration = 0;
  allSelectedOptionValues.forEach(([ci, val]) => {
    const opt = ci.options.find(o => o.uid === val);
    if (opt) {
      newTotalPrice += opt.price;
      newTotalDuration += opt.duration;
    } else if (ci.type === CustomerInputType.Number && ci.unitPrice) {
      newTotalPrice += ci.unitPrice * (parseInt(val) || 0);
    }
  });

  return [newTotalPrice, newTotalDuration];
};

/**
* @returns The number of seconds from the start of the
* appointment that are available to book.
* @param eventTimeBounds  The time bounds for the event
* @param unavailableTimeBoundsForAppointmentLessExistingBooking  The existing
* bookings for the appointment less the existing bookings for the current user,
* if any exist
* @param tailActiveTimeSeconds  The amount of time that should be active after
* the latest booking
*/
export const getAppointmentActiveTimeSeconds = (
  eventTimeBounds: {
    min: number,
    max: number,
  },
  unavailableTimeBoundsForAppointmentLessExistingBooking: Bounds[],
  tailActiveTimeSeconds: number,
): number => {
  const minStart = eventTimeBounds.min as number;

  const appointmentMaxDurationSec = Math.abs(eventTimeBounds.max - minStart);
  const ends = unavailableTimeBoundsForAppointmentLessExistingBooking.map(
    b => b.max
  );
  const maxEnd = ends.length ? Math.max(...ends) : minStart;

  const bookedDurationFromAppointmentStart = Math.abs(maxEnd - minStart);

  const activeSecs =
    Math.abs(tailActiveTimeSeconds) + bookedDurationFromAppointmentStart;

  return Math.min(activeSecs, appointmentMaxDurationSec);
};

export const getShortestServiceTime = (
  serviceOfferings: Pick<NonNullable<ServiceOfferings>[number], 'baseTime'>[],
): number => {
  let lowestServiceTime = 10;
  if (serviceOfferings && serviceOfferings.length > 0) {
    lowestServiceTime = serviceOfferings.reduce(
      (acc, offering) => acc > offering.baseTime ?
        offering.baseTime :
        acc, serviceOfferings[0].baseTime
    );
  }

  return lowestServiceTime;
};

/**
* @returns The number of booking slots that should be available for an
* appointment
*/
export const getAppointmentBookingSlotsCount = (
  bookableTimeSpanMins: number,
  shortestServiceTime: number,
): number => {
  const bookingSlotsCount = Math.floor(
    bookableTimeSpanMins / shortestServiceTime
  );

  return bookingSlotsCount;
};

export const getTimeSlotOverlapsBusyTimeSlot = (
  slot: number,
  totalDurationSeconds: number,
  otherBookingsForAppointmentStartEnd: [number, number][],
  ignoreIsPast: boolean,
): boolean => {
  const start = dayjs.unix(slot);
  const end = start.clone().add(totalDurationSeconds, 's');
  const isPast = ignoreIsPast ? false : start.isBefore(dayjs());
  const isOverlapping = isMomentRangeOverlapWithExistingBooking(
    otherBookingsForAppointmentStartEnd,
    start,
    end,
  );

  const disabled = isPast || isOverlapping;

  return disabled;
};

export const getBookingSlotsUnix = (
  start: dayjs.Dayjs,
  minSlotDuration: number,
  max: number,
  ix: number,
): number[] => {
  if (ix >= max) {
    return [];
  }

  const slot = start.clone()
    .add(Math.abs(minSlotDuration) * ix, 'm').second(0).millisecond(0).unix();
  const chunk = getBookingSlotsUnix(start, minSlotDuration, max, ix + 1);

  return chunk.concat(slot).sort((a, b) => a - b);
};

export const divideIntoTimeSlots = (
  availabilityStartDayJs: dayjs.Dayjs,
  availabilityEndDayJs: dayjs.Dayjs,
  minDurationMinutes: number,
): number[] => {
  const bookingSlotsCount = getAppointmentBookingSlotsCount(
    availabilityEndDayJs.diff(availabilityStartDayJs, 'm'),
    minDurationMinutes,
  );

  const slots = getBookingSlotsUnix(
    availabilityStartDayJs,
    minDurationMinutes,
    bookingSlotsCount,
    0,
  );

  return slots;
};
